// Per-run engineering-validity disclosure (2026-06-12 readability pass).
//
// The VALID / LIMIT BREACH chip is the rollup; this <details> block is
// the numbers behind it — damper-travel used fractions, tyre-envelope
// time fractions, and the platform-sink figures that read alarming
// without context (the sink includes tyre compression, and the rig has
// no ground plane). Bounds arrive as a loose record (the backend owns
// the key set); every row renders defensively so a missing key drops
// the row rather than breaking the card.

type ValidityBlock = {
  overall_state: "OK" | "BREACH" | "NOT_EVALUABLE";
  bounds: Record<string, Record<string, unknown>>;
  limits: Record<string, unknown>;
};

export type RunValidityDetailProps = {
  runId: number;
  validity: ValidityBlock;
  kpiScores: Record<string, unknown> | null;
};

const CORNERS = ["lf", "rf", "lr", "rr"] as const;

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function pct(v: number): string {
  return `${(v * 100).toFixed(0)}%`;
}

/** Worst (max) value of bounds[`${prefix}_${corner}`][field] across corners. */
function worstCorner(
  bounds: ValidityBlock["bounds"], prefix: string, field: string,
): { corner: string; value: number } | null {
  let best: { corner: string; value: number } | null = null;
  for (const c of CORNERS) {
    const v = num(bounds[`${prefix}_${c}`]?.[field]);
    if (v !== null && (best === null || v > best.value)) {
      best = { corner: c.toUpperCase(), value: v };
    }
  }
  return best;
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="validity-detail-row" title={hint}>
      <span className="validity-detail-label">{label}</span>
      <span className="validity-detail-value">{value}</span>
    </div>
  );
}

export function RunValidityDetail({ runId, validity, kpiScores }: RunValidityDetailProps) {
  const b = validity.bounds;
  const travelComp = worstCorner(b, "travel_comp", "used_frac");
  const travelExt = worstCorner(b, "travel_ext", "used_frac");
  const tyreUnload = worstCorner(b, "tire_unload", "frac");
  const tyreOverload = worstCorner(b, "tire_overload", "frac");
  const rhFront = num(b.rh_drop_front?.drop_max_mm);
  const rhRear = num(b.rh_drop_rear?.drop_max_mm);
  const occFront = num(kpiScores?.rh_outside_aero_map_frac_front);
  const occRear = num(kpiScores?.rh_outside_aero_map_frac_rear);

  return (
    <details className="validity-detail" data-testid={`validity-detail-${runId}`}>
      <summary className="validity-detail-summary">Validity detail</summary>
      <div className="validity-detail-body">
        {(travelComp || travelExt) && (
          <div className="validity-detail-section">
            <div className="validity-detail-heading">Damper travel used (worst corner)</div>
            {travelComp && (
              <Row
                label={`Compression (${travelComp.corner})`}
                value={pct(travelComp.value)}
                hint="Fraction of the mechanical bump travel envelope used"
              />
            )}
            {travelExt && (
              <Row
                label={`Extension (${travelExt.corner})`}
                value={pct(travelExt.value)}
                hint="Fraction of the mechanical rebound travel envelope used"
              />
            )}
          </div>
        )}
        {(tyreUnload || tyreOverload) && (
          <div className="validity-detail-section">
            <div className="validity-detail-heading">Tyre load — time outside envelope (worst corner)</div>
            {tyreUnload && (
              <Row
                label={`Unloaded (${tyreUnload.corner})`}
                value={pct(tyreUnload.value)}
                hint="Time fraction below the tyre's characterised minimum load"
              />
            )}
            {tyreOverload && (
              <Row
                label={`Overloaded (${tyreOverload.corner})`}
                value={pct(tyreOverload.value)}
                hint="Time fraction above the tyre's characterised maximum load"
              />
            )}
          </div>
        )}
        {(rhFront !== null || rhRear !== null) && (
          <div className="validity-detail-section">
            <div className="validity-detail-heading">Platform sink vs static (incl. tyre squish)</div>
            {rhFront !== null && <Row label="Front — max" value={`${rhFront.toFixed(1)} mm`} />}
            {rhRear !== null && <Row label="Rear — max" value={`${rhRear.toFixed(1)} mm`} />}
            {occFront !== null && (
              <Row
                label="RH beyond WT-characterised range — front"
                value={pct(occFront)}
                hint="Coverage info: time outside the wind-tunnel TESTED ride-height range — not a legality rule"
              />
            )}
            {occRear !== null && (
              <Row
                label="RH beyond WT-characterised range — rear"
                value={pct(occRear)}
                hint="Coverage info: time outside the wind-tunnel TESTED ride-height range — not a legality rule"
              />
            )}
            <p className="validity-detail-note" data-testid={`validity-note-${runId}`}>
              Sink includes tyre compression (~60–70 mm at &gt;20 kN) and
              prescribed kerb strikes — the virtual rig has no ground
              plane. Large absolute figures are expected on replay laps;
              the gate compares candidates to the Reference run, and the
              WT-range fraction is characterised-coverage info, not a
              pass/fail.
            </p>
          </div>
        )}
      </div>
    </details>
  );
}
